// src/components/analytics/DynamicTableCard.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from './Card';
import { NoDataPlaceholder } from './NoDataPlaceholder';

// This is the crucial part: a generic column definition
export interface ColumnDef<T> {
  header: string;
  colSpan: number; // For grid layout (e.g., 1, 2, 3)
  // A render function that takes a single row of data and returns JSX
  cell: (row: T) => React.ReactNode; 
}

// The props for our generic component
interface DynamicTableCardProps<T> {
  title: string;
  data: T[];
  columns: ColumnDef<T>[];
  viewMoreLink: string;
  // A function to get a unique key for each row for React's map function
  getUniqueKey: (item: T, index: number) => string | number;
}

export const DynamicTableCard = <T extends {}>({
  title,
  data,
  columns,
  viewMoreLink,
  getUniqueKey,
}: DynamicTableCardProps<T>) => {
  const { t } = useTranslation('Common');
  const navigate = useNavigate();
  const hasData = data && data.length > 0;

  return (
    <Card title={title}>
      <div>
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2">
          {columns.map((col) => (
            <div key={col.header} className={`col-span-${col.colSpan}`}>
              {col.header}
            </div>
          ))}
        </div>
        
        {/* Table Body */}
        {hasData ? (
          <>
            <div className="space-y-1">
              {data.map((row, index) => (
                <div 
                  key={getUniqueKey(row, index)} 
                  className="grid grid-cols-12 gap-4 items-center bg-card-color-100 dark:bg-gray-800 hover:bg-card-color-200 dark:hover:bg-gray-700 rounded-lg px-4 py-3 text-sm"
                >
                  {columns.map((col) => (
                    <div key={col.header} className={`col-span-${col.colSpan}`}>
                      {col.cell(row)} {/* Use the render function here */}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="pt-4 flex justify-end">
              <button 
                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" 
                onClick={() => navigate(viewMoreLink)}
              >
                {t('viewMore')}
              </button>
            </div>
          </>
        ) : (
          // <div className="text-center py-10 text-gray-400 dark:text-gray-500">
          //   <p>No recent {title.toLowerCase()} found.</p>
          // </div>
          <NoDataPlaceholder className="h-80" />
        )}
      </div>
    </Card>
  );
};